import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { userApi } from "../../config";
import { Clock, Video, Ticket, MapPin, ChevronRight, ChevronLeft, ChevronDown, Filter, BookOpen, Award, Search, X, RotateCcw } from "lucide-react";
import { motion } from "framer-motion";
import SEO from "../SEO";

const SERVICES_CACHE_TTL_MS = 10 * 60 * 1000;

const getServicesCacheKey = (tab: string, category: string, page: number, limit: number, search: string) =>
  `services-page-cache-v8:${tab}:${category}:${page}:${limit}:${search}`;

const readServicesCache = (key: string) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > SERVICES_CACHE_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeServicesCache = (key: string, payload: any) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        ts: Date.now(),
        payload,
      }),
    );
  } catch {
    // Ignore quota / private mode failures.
  }
};

interface SubCategory {
  name: string;
  slug: string;
  isActive: boolean;
}

interface Category {
  _id: string;
  name: string;
  slug: string;
  isActive: boolean;
  subCategories: SubCategory[];
}

interface ClassPrice {
  session?: string;
  monthly?: string;
  yearly?: string;
}

interface Session {
  _id: string;
  classTitle: string;
  classDescription: string;
  classStartDate?: string;
  classDuration: string;
  classCategory: string;
  classThumbnail?: string;
  classPrice: ClassPrice;
  purchaseStatus: string;
  pricingType?: string;
  serviceType: "yoga" | "regular" | "one-to-one";
  instructorName: string;
  instructorThumbnail?: string;
}

export default function ServicesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Tabs: "regular-classes" vs "one-to-one-consultation"
  const [activeTab, setActiveTab] = useState<string>("regular-classes");
  // Subcategory filters within the active tab
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>("all");
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const ITEMS_PER_PAGE = 6;
  const [totalPages, setTotalPages] = useState<number>(1);
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      const cacheKey = getServicesCacheKey(activeTab, selectedSubCategory, currentPage, ITEMS_PER_PAGE, debouncedSearch);
      const cached = readServicesCache(cacheKey);

      if (cached?.payload) {
        setCategories(cached.payload.categories || []);
        setSessions(cached.payload.sessions || []);
        setTotalPages(cached.payload.totalPages || 1);
        setError(null);
        setLoading(false);
        return;
      }

      try {
        const response = await userApi.get<any>(`/fetch-services-page-data?tab=${activeTab}&category=${encodeURIComponent(selectedSubCategory)}&page=${currentPage}&limit=${ITEMS_PER_PAGE}&search=${encodeURIComponent(debouncedSearch)}`);
        if (!isMounted) return;
        if (response.data && response.data.success) {
          const payload = {
            categories: response.data.categories || [],
            sessions: response.data.classes || [],
            totalPages: response.data.totalPages || 1,
          };
          setCategories(response.data.categories || []);
          setSessions(response.data.classes || []);
          setTotalPages(response.data.totalPages || 1);
          setError(null);
          writeServicesCache(cacheKey, payload);
        } else {
          throw new Error("Failed to load services");
        }
      } catch (err: any) {
        if (!isMounted) return;
        console.error("Error fetching services:", err);
        if (!cached?.payload) {
          setError("Unable to load services at this time. Please try again later.");
        }
      } finally {
        if (!isMounted) return;
        if (!cached?.payload) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [activeTab, selectedSubCategory, currentPage, debouncedSearch]);

  // Since we fetch from backend with search, we don't need to filter sessions locally anymore
  const filteredSessions = sessions;

  // Reset filter selections and current page
  const handleTabChange = (tabSlug: string) => {
    setActiveTab(tabSlug);
    setSelectedSubCategory("all");
    setCurrentPage(1);
  };

  const handleSubCategoryChange = (subName: string) => {
    setSelectedSubCategory(subName);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedSubCategory("all");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPriceDisplay = (price: ClassPrice, purchaseStatus: string) => {
    if (purchaseStatus === "Free Tier") return "Free";
    if (price?.session && Number(price.session) > 0) return `₹${price.session}`;
    if (price?.monthly && Number(price.monthly) > 0) return `₹${price.monthly}/mo`;
    if (price?.yearly && Number(price.yearly) > 0) return `₹${price.yearly}/yr`;
    return "Free";
  };

  return (
    <div className="min-h-screen font-sans pb-20 bg-white" style={{ backgroundColor: "#FFFFFF" }}>
      {/* Premium Gradient Hero Header */}
      {/* <div className="relative bg-gradient-to-br from-teal-900 via-emerald-800 to-cyan-900 text-white overflow-hidden py-12 md:py-20 px-4 sm:px-8 lg:px-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(16,185,129,0.1),transparent)] pointer-events-none" />
        <div className="absolute top-0 right-0 w-72 h-72 md:w-96 md:h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10 space-y-3 md:space-y-4">
          <div className="inline-flex items-center space-x-1.5 bg-emerald-500/20 text-emerald-300 px-3.5 py-1 rounded-full text-[10px] md:text-xs font-semibold uppercase tracking-wider border border-emerald-400/20">
            <Sparkles className="h-3 md:h-3.5 w-3 md:w-3.5 text-emerald-450 animate-pulse" />
            <span>Discover Wellness</span>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight bg-gradient-to-r from-white via-teal-100 to-emerald-250 bg-clip-text text-transparent">
            Holistic Services
          </h1>
          <p className="text-xs md:text-base text-teal-100/90 max-w-xl mx-auto leading-relaxed">
            Select immersive group classes or book direct consultations with clinical mentors.
          </p>
        </div>
      </div> */}

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-12">
        <SEO
          title="Wellness Services, Classes & Consultations | MetaFit Wellness"
          description="Explore and book live regular yoga classes, 1-on-1 consultations, meditation, ayurveda, and holistic wellness sessions on MetaFit Wellness."
          keywords="wellness services, yoga classes online, 1 on 1 wellness consultation, online meditation, ayurvedic doctor consult, holistic healing India"
          url="https://www.metafitwellness.com/services"
          canonical="https://www.metafitwellness.com/services"
          image="https://www.metafitwellness.com/metafit.webp"
        />
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-7 md:mb-9"
        >
          <p className="text-[11px] font-mono font-semibold uppercase tracking-[0.18em] text-teal-700 mb-1">Explore wellness</p>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">Find the right session for you</h1>
          <p className="mt-1 text-xs md:text-sm text-slate-500 max-w-2xl">Browse guided classes and personal consultations from trusted wellness mentors.</p>
        </motion.div>
        <div className="flex flex-col md:flex-row gap-6 lg:gap-8 items-start">
        {/* Left Sidebar Filter (collapsible on mobile) */}
        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className={`w-full md:w-[280px] shrink-0 transition-all z-10 md:sticky md:top-24 md:self-start ${showFilters ? 'block' : 'hidden md:block'}`}
        >
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200 max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                <Filter className="w-5 h-5 text-teal-600" />
                Filters
              </h3>
              <button 
                className="md:hidden p-1.5 hover:bg-gray-100 rounded-lg text-gray-500" 
                onClick={() => setShowFilters(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Search Box */}
            <div className="mb-6 relative">
              <input 
                type="text" 
                placeholder="Search sessions..." 
                className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all placeholder-gray-400"
                value={searchQuery}
                onChange={(e) => {
                   setSearchQuery(e.target.value);
                   setCurrentPage(1);
                }}
              />
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-3.5" />
            </div>

            {/* Categories List */}
            <div className="flex items-center justify-between mb-3 px-1">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Categories</h4>
              {(selectedSubCategory !== "all" || searchQuery) && (
                <button onClick={clearFilters} className="flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-900">
                  <RotateCcw className="h-3 w-3" /> Reset
                </button>
              )}
            </div>
            <div className="space-y-2">
              <button
                onClick={() => handleSubCategoryChange("all")}
                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${selectedSubCategory === "all" ? "bg-teal-50 text-teal-700 font-bold shadow-sm" : "text-gray-600 hover:bg-gray-50 font-medium hover:text-gray-900"}`}
              >
                All Programs
              </button>
              
              {categories.filter((cat) => cat.isActive).map((cat) => {
                const activeSubs = (cat.subCategories || []).filter((sub) => sub.isActive);
                const isOpen = openCategory === cat.slug;
                const isCategorySelected = selectedSubCategory.toLowerCase() === cat.name.toLowerCase();
                const hasSelectedSub = activeSubs.some((sub) => selectedSubCategory.toLowerCase() === sub.name.toLowerCase());
                return (
                <div key={cat.slug} className={`rounded-xl border transition-colors ${isOpen || isCategorySelected || hasSelectedSub ? "border-teal-100 bg-teal-50/40" : "border-transparent bg-gray-50/70"}`}>
                  <button
                    onClick={() => setOpenCategory(isOpen ? null : cat.slug)}
                    aria-expanded={isOpen}
                    aria-controls={`category-${cat.slug}`}
                    className={`w-full flex items-center justify-between gap-3 text-left px-3 py-3 rounded-xl text-sm font-semibold transition-all ${isCategorySelected || hasSelectedSub ? "text-teal-700" : "text-gray-700 hover:text-teal-700"}`}
                  >
                    <span>{cat.name}</span>
                    <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180 text-teal-600" : "text-gray-400"}`} />
                  </button>
                  <div id={`category-${cat.slug}`} className={`grid transition-all duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                    <div className="overflow-hidden">
                      <div className="px-2 pb-2 space-y-1">
                        <button
                          onClick={() => handleSubCategoryChange(cat.name)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${isCategorySelected ? "bg-white text-teal-700 font-bold shadow-sm" : "text-gray-500 hover:bg-white hover:text-gray-900"}`}
                        >
                          All {cat.name}
                        </button>
                      {activeSubs.map((sub) => (
                        <button
                          key={sub.slug}
                          onClick={() => handleSubCategoryChange(sub.name)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all duration-200 ${selectedSubCategory.toLowerCase() === sub.name.toLowerCase() ? "bg-white text-teal-700 font-bold shadow-sm" : "text-gray-500 hover:bg-white hover:text-gray-900"}`}
                        >
                          {sub.name}
                        </button>
                      ))}
                      </div>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          </div>
        </motion.div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 w-full">
          {/* Sleek Tab Switcher */}
          <div className="flex items-center justify-between gap-3 mb-6 md:mb-8">
            <div className="bg-white p-1.5 rounded-xl md:rounded-2xl shadow-sm border border-gray-200 flex space-x-1 w-full max-w-xl">
              <button
                onClick={() => handleTabChange("regular-classes")}
                className={`flex-1 py-1.5 md:py-2 px-3 md:px-5 rounded-lg md:rounded-xl text-xs md:text-xs font-semibold transition-all duration-200 flex items-center justify-center space-x-1.5 ${activeTab === "regular-classes"
                  ? "bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-md shadow-teal-600/20"
                  : "text-gray-500 hover:bg-gray-50 hover:text-teal-600"
                  }`}
              >
                <BookOpen className="h-4 w-4 shrink-0" />
                <span>Regular Classes</span>
              </button>
              <button
                onClick={() => handleTabChange("one-to-one-consultation")}
                className={`flex-1 py-1.5 md:py-2 px-3 md:px-5 rounded-lg md:rounded-xl text-xs md:text-xs font-semibold transition-all duration-200 flex items-center justify-center space-x-1.5 ${activeTab === "one-to-one-consultation"
                  ? "bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-md shadow-teal-600/20"
                  : "text-gray-500 hover:bg-gray-50 hover:text-teal-600"
                  }`}
              >
                <Award className="h-4 w-4 shrink-0" />
                <span>1-on-1 Consultation</span>
              </button>
            </div>
            
            <button
              onClick={() => setShowFilters((prev) => !prev)}
              aria-expanded={showFilters}
              aria-label="Toggle filters"
              className={`md:hidden relative flex items-center justify-center h-[46px] w-[46px] rounded-xl shadow-sm shrink-0 border transition-all duration-200 ${showFilters
                ? "bg-gradient-to-br from-teal-600 to-emerald-600 border-transparent text-white"
                : "bg-white border-gray-150 text-teal-650 hover:border-teal-400"
                }`}
            >
              <Filter className="h-4 w-4" />
            </button>
          </div>

          {(selectedSubCategory !== "all" || searchQuery) && (
            <div className="flex flex-wrap items-center gap-2 mb-6 text-xs">
              <span className="text-gray-500 font-medium">Active filters:</span>
              {selectedSubCategory !== "all" && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-100 px-3 py-1.5 font-semibold text-teal-800">
                  {selectedSubCategory}
                  <button onClick={() => handleSubCategoryChange("all")} aria-label="Remove category filter"><X className="h-3 w-3" /></button>
                </span>
              )}
              {searchQuery && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 font-semibold text-gray-700">
                  “{searchQuery}”
                  <button onClick={() => setSearchQuery("")} aria-label="Clear search"><X className="h-3 w-3" /></button>
                </span>
              )}
            </div>
          )}

        {/* Loading Spinner */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-600"></div>
          </div>
        )}

        {/* Error Message */}
        {error && !loading && (
          <div className="text-center bg-red-50 border border-red-200 p-6 md:p-8 rounded-xl md:rounded-2xl max-w-xl mx-auto my-12">
            <p className="text-red-700 font-medium text-sm md:text-base">{error}</p>
          </div>
        )}

        {/* Dynamic Services Grid */}
        {!loading && !error && (
          <>
            {filteredSessions.length === 0 ? (
              <div className="text-center py-16 md:py-20 bg-white rounded-xl md:rounded-2xl border border-gray-150 shadow-sm max-w-md mx-auto">
                <div className="bg-teal-50 h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-3 text-teal-650">
                  <BookOpen className="h-5 w-5" />
                </div>
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-1">No sessions available</h3>
                <p className="text-gray-400 text-xs px-4">
                  No active programs found under this filter. Try checking different tabs or pills.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
                  {filteredSessions.map((session, index) => (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.4, delay: index * 0.08 }}
                      key={session._id}
                      className="group bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-slate-200/90 shadow-xs hover:shadow-md hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between cursor-pointer"
                    >
                      {/* Wide 16:9 Thumbnail Header matching Treatments card */}
                      <div className="aspect-[16/9] relative overflow-hidden w-full bg-slate-100">
                        {session.classThumbnail ? (
                          <img
                            src={session.classThumbnail}
                            alt={session.classTitle}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-teal-700 via-emerald-700 to-cyan-800" />
                        )}

                        {/* Duration Pill (Top-Left) */}
                        {session.classDuration && (
                          <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-xs text-white text-[9.5px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1 z-20 border border-white/10 shadow-xs">
                            <Clock className="w-2.5 h-2.5 text-[#FF6B35]" />
                            <span>{session.classDuration}</span>
                          </div>
                        )}

                        {/* Category / Tier Pill (Top-Right) */}
                        <div className="absolute top-2 right-2 bg-white/95 text-slate-800 text-[9px] font-semibold px-2 py-0.5 rounded-full shadow-xs z-20">
                          {session.classCategory || session.purchaseStatus}
                        </div>
                      </div>

                      {/* Compact Card Body matching Image 2 */}
                      <div className="p-3 sm:p-3.5 flex flex-col bg-white">
                        {/* Title */}
                        <h3 className="font-semibold text-[13px] sm:text-[13.5px] text-slate-900 line-clamp-1 group-hover:text-teal-700 transition-colors leading-snug">
                          {session.classTitle}
                        </h3>

                        {/* Mentor & Mode row (matches Location row in Treatments) */}
                        <div className="flex items-center justify-between text-[11px] text-slate-500 mt-1">
                          <span className="flex items-center gap-1.5 truncate max-w-[190px]">
                            {session.instructorThumbnail ? (
                              <img src={session.instructorThumbnail} alt="" className="w-4 h-4 rounded-full object-cover shrink-0" />
                            ) : (
                              <div className="w-4 h-4 rounded-full bg-teal-600 text-white flex items-center justify-center text-[8px] font-bold shrink-0">
                                {session.instructorName.charAt(0)}
                              </div>
                            )}
                            <span className="truncate">{session.instructorName}</span>
                          </span>
                          <span className="flex items-center gap-1 shrink-0 text-slate-400 text-[10px]">
                            <Video className="w-2.5 h-2.5 text-emerald-600" />
                            <span>Online</span>
                          </span>
                        </div>

                        {/* Price & Book Row (matches Ticket price row in Treatments) */}
                        <div className="flex items-center justify-between text-slate-800 font-semibold text-[12px] mt-2 pt-2 border-t border-slate-100">
                          <div className="flex items-center gap-1">
                            <Ticket className="w-3 h-3 text-slate-400 shrink-0" />
                            <span className="font-bold text-slate-900 text-[13px]">
                              {getPriceDisplay(session.classPrice, session.purchaseStatus)}
                            </span>
                          </div>
                          <Link
                            to={`/booking/${session.instructorName.replace(/\s+/g, '-').toLowerCase()}/step1?classId=${session._id}`}
                            className="book-session-btn inline-flex items-center gap-1 px-3 py-1 text-[11px] font-semibold text-white bg-black hover:bg-neutral-800 rounded-full transition-all shadow-xs"
                            style={{ color: '#FFFFFF', backgroundColor: '#000000' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span style={{ color: '#FFFFFF' }}>Book</span>
                            <ChevronRight className="w-2.5 h-2.5" style={{ color: '#FFFFFF' }} />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center mt-12 space-x-2">
                    <button
                      onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg border text-gray-500 transition-colors ${currentPage === 1
                        ? "opacity-50 cursor-not-allowed bg-gray-100 border-gray-200"
                        : "hover:bg-gray-50 border-gray-300 hover:text-teal-600"
                        }`}
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>

                    {Array.from({ length: totalPages }, (_, index) => {
                      const pageNum = index + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-9 h-9 rounded-lg text-xs font-semibold transition-all duration-150 border ${currentPage === pageNum
                            ? "bg-teal-600 border-teal-600 text-white shadow-sm"
                            : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-teal-600"
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-lg border text-gray-500 transition-colors ${currentPage === totalPages
                        ? "opacity-50 cursor-not-allowed bg-gray-100 border-gray-200"
                        : "hover:bg-gray-50 border-gray-300 hover:text-teal-600"
                        }`}
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
        </div>
        </div>
      </div>
    </div>
  );
}
